import { Link } from "react-router-dom";
import Room from "@/components/Room";

export default function Intelligence() {
  return (
    <Room title="Macro Opportunity" description="Briefs that read a market against the biology beneath it.">
      <div className="fade">
        <div className="kicker">Intelligence · Briefs</div>
        <h1 data-testid="intelligence-h1">Upstream of the price.</h1>
        <div className="goldrule"></div>
        <p className="lede">Every brief is different. What unites them is the model underneath: a reading across biology, time, location and capital that finds where a market and the living system beneath it have parted company. Sometimes the cause is a misclassification. Sometimes it is a supply disruption three steps removed and years from the price. The brief puts in the hands of its holder a position nobody else is holding.</p>
      </div>

      <section className="q">
        <div className="n">01</div>
        <h2>Every price assumes something about a living system. Which of yours has been tested?</h2>
        <p>A palm oil price assumes palms that can still be harvested. A loan against a forest assumes trees that stand for its full term. A supply contract assumes the farmer is still there in year five. None of these assumptions is written down, so none of them is checked, and each one is invisible until the moment it fails.</p>
        <p>By then the price has moved and the position has already been decided by someone else. What does your book assume about the ground it stands on, and who last looked?</p>
      </section>

      <section className="q">
        <div className="n">02</div>
        <h2>What does a brief put in your hands?</h2>
        <p>SubPalm is one example. For thirty years oil palm output and biological age moved together, because the industry was young. The expansion planted between 1988 and 2002 is now 24 to 38 years old and ageing at once. Past about twenty metres a palm can no longer be harvested by hand, and a tree beyond its productive life does not come back.</p>
        <div className="stats">
          <div className="stat"><b>9m ha</b><span>global palm estate at Class III or worse</span></div>
          <div className="stat"><b>6.2%</b><span>compound annual yield decline in the ageing cohort</span></div>
          <div className="stat"><b>~5%</b><span>of the USD 17 to 28bn replant bill deployed</span></div>
        </div>
        <p className="src">MAATTR Intelligence, SubPalm: The Biological Supply Trade, May 2026.</p>
        <p>The operators face one choice with two outcomes: commit a decade of capital to replanting with dividends suspended, or defer and reach the end of the decade with estates that cannot be harvested. The disclosure prices the deferral. The brief prices the biology, and leaves the holder to decide which side of that gap to stand on.</p>
      </section>

      <section className="q">
        <div className="n">03</div>
        <h2>Who holds a brief?</h2>
        <ul className="plain">
          <li><strong>The holder of exposure</strong>, who wants the fault lines in a book before they show in its returns.</li>
          <li><strong>The hedger</strong>, who needs to know what a position is carrying that no model has diagnosed.</li>
          <li><strong>The allocator</strong>, looking for deep pools of value that the market has not yet seen, because it is not yet looking.</li>
        </ul>
        <p>Briefs are also written to order, against a named portfolio, supply chain or question. Every figure carries its status: confirmed, directional or unquantified. Each copy is numbered to its holder. A brief states position and offers no advice; the decision stays with the person holding it.</p>
      </section>

      <section className="close">
        <p className="ask">If biological age sat on your balance sheet tomorrow, which of your holdings would not change value? And knowing which would, how fast would you want to exit, and how fast would you want to acquire?</p>
        <Link className="write" to="/enter/macro-opportunity" data-testid="intelligence-write">Request a brief</Link>
      </section>
    </Room>
  );
}
